/**
 * Home's Adoption section: what people and their agents did over the last 30 days (four numbers,
 * a quiet list of what it meant, one line of trend) and the monthly benefits statement to
 * download. Administrators and portfolio managers only: the API refuses everyone else and the
 * section is then not shown.
 */

import { monthYear } from '@aioe/shared/wording/dateTime';
import { ArrowDownToLine, CircleCheck, Display, Persons, Pulse } from '@gravity-ui/icons';
import { useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { download, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListRow, SectionHeading, Stat } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const tone = VIEW_TONES.home!;

/** The last twelve calendar months, newest first, as `YYYY-MM` keys with their words. */
function monthOptions(): Record<string, string> {
  const out: Record<string, string> = {};
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out[`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`] = monthYear(d, { timeZone: 'UTC' });
  }
  return out;
}

/** Minutes as a person says them: 45 minutes, 3 hours, 2 days. */
function waited(minutes: number): string {
  if (minutes < 90) return plural(Math.round(minutes), 'minute');
  if (minutes < 48 * 60) return plural(Math.round(minutes / 60), 'hour');
  return plural(Math.round(minutes / 1440), 'day');
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;

export function AdoptionPanel() {
  const a = useLoad(() => routes.adoption(30).catch(() => undefined), [], 300_000);
  const { format } = useMoney();
  const months = monthOptions();
  const [month, setMonth] = useState(Object.keys(months)[1]!);
  const [kind, setKind] = useState<'json' | 'csv'>('csv');
  const [error, setError] = useState<string | undefined>();
  // Not for you (the API refused), or nothing yet: nothing is drawn.
  if (!a.data) return null;
  const d = a.data;
  const cost = (n: number | undefined) => (n === undefined ? '—' : format(n / (d.cost.total && d.cost.totalUsd ? d.cost.total / d.cost.totalUsd : 1)));

  const lines: string[] = [];
  if (d.work.approvalsWaited.medianMinutes !== undefined) lines.push(`Approvals waited a median of ${waited(d.work.approvalsWaited.medianMinutes)}`);
  if (d.cost.perTaskDone !== undefined) lines.push(`Cost per task done ${cost(d.cost.perTaskDone)}`);
  if (d.agents.teamsUsed || d.agents.specialistsUsed) lines.push(`${plural(d.agents.teamsUsed, 'team')} and ${plural(d.agents.specialistsUsed, 'specialist')} used`);
  if (d.work.pullRequestsMerged) lines.push(`${plural(d.work.pullRequestsMerged, 'pull request')} merged`);

  const save = async () => {
    setError(undefined);
    try {
      await download(routes.adoptionStatementUrl(month, kind), `benefits-${month}.${kind}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <section className="list-group-section" aria-label="Adoption">
      <SectionHeading title="Adoption" description={`What people and their agents did in the last ${d.window.days} days.`} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Persons />} label="People active" value={d.people.activeWindow} detail={`${d.people.activeWeek} this week, of ${d.people.members}`} />
        <Stat tone={tone} icon={<Display />} label="Machines" value={d.machines.reportedWindow} detail={`reporting, of ${d.machines.enrolled} enrolled`} />
        <Stat tone={tone} icon={<Pulse />} label="Runs" value={d.agents.runs} detail={`${d.agents.byOutcome.ok} finished well`} />
        <Stat tone={tone} icon={<CircleCheck />} label="Tasks done" value={d.work.tasksDone} detail={plural(d.work.plansDone, 'plan') + " done"} />
      </div>
      <div className="list-group">
        {lines.length ? (
          <ListBlock>
            <ul className="m-0 grid list-none gap-1 p-0 text-sm text-muted tabular-nums">
              {lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </ListBlock>
        ) : null}
        {d.trend.some((t) => t.runs || t.tasksDone) ? (
          <ListBlock className="grid gap-1">
            <span className="text-xs text-muted">Runs a day</span>
            <div className="h-24" role="img" aria-label="Runs a day over the window">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.trend} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <XAxis dataKey="day" hide />
                  <Tooltip formatter={(v) => [String(v), 'Runs']} labelFormatter={(l) => String(l)} />
                  <Area type="monotone" dataKey="runs" stroke="var(--tone-teal, var(--accent))" fill="var(--tone-teal, var(--accent))" fillOpacity={0.18} strokeWidth={1.5} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ListBlock>
        ) : null}
        <ListRow
          icon={<ArrowDownToLine />}
          title="Benefits statement"
          description="A month of adoption, cost and outcomes, to download."
          control={
            <>
              <ChoiceSelect value={month} options={months} onChange={setMonth} label="Month" className="w-fit min-w-40" />
              <ChoiceSelect value={kind} options={{ csv: 'CSV', json: 'JSON' }} onChange={setKind} label="Format" className="w-fit min-w-24" />
              <Button size="sm" variant="secondary" onPress={() => void save()}>
                Download
              </Button>
            </>
          }
        >
          {error ? <ErrorNote error={error} /> : null}
        </ListRow>
      </div>
    </section>
  );
}
